import { Component } from "react";
import "./JoinAction.css";
import PropTypes from "prop-types";
import Action from "./../../formElements/Action/Action";
import { logInfo, setLessonTag } from "./../../../services/logging";
import { Translation } from "react-i18next";

const BTN_TYPE = "button";

const DISABLED_PERIOD = 5; // 5 seconds

/**
 * `JoinAction` is used to render action "Join Now". It allows users to join the Talaera video chat.
 *
 * When joining is not possible (lesson is not ready), action is rendered as disabled.
 * It also becomes disabled for 5 seconds after a click (to avoid multiple initialization attempts of
 * Talaera video chat).
 *
 * If joining is possible, it opens a new browser tab with Talaera video chat page.
 */
export default class JoinAction extends Component {
  constructor(props) {
    super(props);

    this.state = {
      label: "Join Now",
      secondsToEnabled: DISABLED_PERIOD,
      disabled: false,
    };

    this.handleClick = this.handleClick.bind(this);
    this.tick = this.tick.bind(this);
  }

  componentWillUnmount() {
    this.unmounted = true;
  }

  handleClick(event) {
    if (!this.props.lesson.validForJoining) {
      event.preventDefault();
      this.props.onNotValid && this.props.onNotValid();
      return false;
    }
    if (
      (this.props.lesson.joinAction && this.props.lesson.joinAction.disabled) ||
      this.state.disabled
    ) {
      // Do nothing if disabled.
      event.preventDefault();
      return false;
    }
    setLessonTag(this.props.lesson.id);
    logInfo("Clicked on 'Join now'", {
      from: "handleClick",
      session_storage: JSON.stringify(sessionStorage),
    });
    // Need to save sessionStorage for another browser tab.
    localStorage.setItem("sessionStorage", JSON.stringify(sessionStorage));
    this.tick();
  }

  tick() {
    if (this.unmounted) {
      return;
    }

    let label;
    let disabled;
    if (this.state.secondsToEnabled === 0) {
      label = "Join Now";
      disabled = false;

      this.setState({
        label: label,
        secondsToEnabled: DISABLED_PERIOD,
        disabled: disabled,
      });
    } else {
      label = `Join in (${this.state.secondsToEnabled})`;
      disabled = true;

      this.setState({
        label: label,
        secondsToEnabled: this.state.secondsToEnabled - 1,
        disabled: disabled,
      });
      setTimeout(this.tick, 1000);
    }

    this.props.lesson.joinAction = {
      label: label,
      disabled: disabled,
    };
    // We have 2 places with Join Now action and we need to disable/enable in both places at the same time.
    this.props.updateItem && this.props.updateItem(this.props.lesson);
  }

  getTooltipText() {
    if (!this.props.lesson.validForJoining) {
      return "This button will be enabled when it’s time to join your lesson";
    }
  }

  render() {
    const { label, disabled } = this.props.lesson.joinAction
      ? this.props.lesson.joinAction
      : this.state;
    const className =
      "btn join-now" +
      (this.props.type === BTN_TYPE ? " button" : "") +
      (disabled || !this.props.lesson.validForJoining ? " disabled" : "");
    return (
      <Action
        className={className}
        href={"/conversation/" + this.props.lesson.id}
        target="_blank"
        rel="noopener noreferrer"
        title={this.getTooltipText()}
        onClick={this.handleClick}
        onDoubleClick={this.handleClick}
      >
        <Translation>{t => t("Common." + label)}</Translation>
      </Action>
    );
  }
}

JoinAction.propTypes = {
  /** Lesson object that should contain lesson’s id (from the server), it will be used to redirect
   * user to the corresponding Conversation page. It also should contain boolean attribute `validForJoining`
   * that is used for defining whether the action is disabled or not.
   */
  lesson: PropTypes.shape({
    /** Lesson id from the server. */
    id: PropTypes.string.isRequired,
    /** Boolean value to specify whether lesson is ready for joining. */
    validForJoining: PropTypes.bool.isRequired,
  }).isRequired,
  /**
   * Type of action: if equals to `"button"`, then it will be rendered as a button.
   * It is rendered as a link by default (if this property is not specified).
   */
  type: PropTypes.string,
  /** Optional handler for clicks when joining is not valid. */
  onNotValid: PropTypes.func,
  /** Function to update the container component; it is helpful for keeping several JoinActions in sync. */
  updateItem: PropTypes.func,
};
